/**
 * The visual vocabulary of the case view.
 *
 * The dropdown every filter is made of, the brand marks of the three apps a
 * case lives in (HubSpot, Parallel, Drive), and the little popup that explains
 * what to do when one of those links is missing.
 *
 * Same reason as `format.ts`: a panel that moves into its own file must not
 * have to import its buttons back from the 3,600-line view it left. This
 * module only imports downwards — components, integrations, and format.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Lang } from '@/data/types';
import { departmentKey } from '@/data/workers';
import { KBIcon } from '@/components/kbIcons';
import { AnchoredPop } from '@/components/AnchoredPop';
import { friendlyError } from '@/lib/friendlyError';
import { useCaseDateMutations } from '@/hooks/useCaseLog';
import { T } from './format';
import { PersonFace } from '@/lib/peoplePhotos';

export interface DDOption {
  value: string;
  label: string;
  sub?: string;
  avatar?: string;
  /** A fixed fact about the option, shown as a chip — never a filter. */
  tag?: string;
}
/**
 * What the filter box says when it is empty.
 *
 * It used to say the number of options — which renders as "13…" and sits in
 * the box looking exactly like text somebody already typed. A placeholder is
 * an instruction, not a statistic; the count belongs to the list below.
 * The portal's language lives in localStorage (App writes it there), and this
 * component is used by seven callers that do not carry a `lang` prop — read
 * it here rather than thread one through all of them for one string.
 */
function findHint(): string {
  let l = 'en';
  try { l = JSON.parse(localStorage.getItem('lexidy-kb-lang') || '"en"'); } catch { /* default */ }
  return l === 'es' ? 'Escribe para filtrar…'
    : l === 'fr' ? 'Filtrer…'
      : l === 'pt' ? 'Escreva para filtrar…'
        : 'Type to filter…';
}

/** Reusable, polished dropdown (opens downward) — used for every filter. */
export function FilterDropdown({
  caption,
  value,
  options,
  onChange,
  minWidth = 160,
}: {
  caption: string;
  value: string;
  options: DDOption[];
  onChange: (v: string) => void;
  minWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  /* Services and HubSpot stages run to dozens of entries across six countries.
     Past a screenful, scrolling is not finding: the menu grows a box you type
     into, accent-blind, so "etat civil" reaches "État civil". */
  const [find, setFind] = useState('');
  useEffect(() => { if (!open) setFind(''); }, [open]);
  const fold = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const searchable = options.length > 8;
  const shown = searchable && find.trim()
    ? options.filter((o) => fold(o.label).includes(fold(find.trim())) || o.value === value)
    : options;
  const cur = options.find((o) => o.value === value) || options[0];
  return (
    <div className="mw-fd" style={{ minWidth }}>
      <span className="mw-fd-cap">{caption}</span>
      <div className="mw-dd">
        <button className="mw-dd-btn" onClick={() => setOpen((o) => !o)} disabled={options.length === 0}>
          {cur?.avatar && <span className="mw-dd-av"><PersonFace who={{ id: cur.value, name: cur.label }}>{cur.avatar}</PersonFace></span>}
          {/* The tag is NOT repeated on the closed button. Whoever is selected
              already has their department stated once, right below — saying it
              twice in the same card is noise, not emphasis. It stays in the
              open list, where it tells people apart. */}
          <span className="mw-dd-text">
            <span className="mw-dd-name">{cur?.label ?? '—'}</span>
            {cur?.sub && <span className="mw-dd-role">{cur.sub}</span>}
          </span>
          <span className={`mw-dd-caret ${open ? 'open' : ''}`}>▾</span>
        </button>
        {open && (
          <>
            <div className="mw-dd-scrim" onClick={() => setOpen(false)} />
            <div className="mw-dd-menu" role="listbox">
              {searchable && (
                <div className="mw-dd-find">
                  <input
                    autoFocus
                    value={find}
                    onChange={(e) => setFind(e.target.value)}
                    /* NOT the option count. It rendered as "13…", which sits
                       in the box looking like text somebody already typed —
                       reported as exactly that. A placeholder says what to do
                       with the field; the count is on the list below it. */
                    placeholder={findHint()}
                    aria-label={caption}
                  />
                  {find && <button onClick={() => setFind('')} aria-label="clear">×</button>}
                </div>
              )}
              {shown.length === 0 && <div className="mw-dd-none">—</div>}
              {shown.map((o) => (
                <button
                  key={o.value}
                  className={`mw-dd-item ${o.value === value ? 'active' : ''}`}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  {o.avatar && <span className="mw-dd-av"><PersonFace who={{ id: o.value, name: o.label }}>{o.avatar}</PersonFace></span>}
                  <span className="mw-dd-text">
                    <span className="mw-dd-name">{o.label}</span>
                    {(o.sub || o.tag) && (
                      <span className="mw-dd-role">
                        {o.tag && <em className={`mw-dep dep-${departmentKey(o.tag)}`}>{o.tag}</em>}
                        {o.sub}
                      </span>
                    )}
                  </span>
                  {o.value === value && <span className="mw-dd-check">✓</span>}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** A small note AT the button you tapped — not a toast across the bottom of
 *  the screen. One line with the fix, one line with who to ask. */
export function LinkHelpPop({ at, kind, lang, onClose }: {
  at: { x: number; y: number }; kind: 'hs' | 'pl'; lang: Lang; onClose: () => void;
}) {
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const away = () => onClose();
    document.addEventListener('keydown', key);
    document.addEventListener('mousedown', away);
    window.addEventListener('scroll', away, true);
    return () => {
      document.removeEventListener('keydown', key);
      document.removeEventListener('mousedown', away);
      window.removeEventListener('scroll', away, true);
    };
  }, [onClose]);
  const W = 268;
  const left = Math.min(Math.max(8, at.x - W / 2), window.innerWidth - W - 8);
  const up = at.y > window.innerHeight - 190;
  return createPortal(
    <div
      className="mw-linkpop"
      style={{ left, width: W, top: up ? undefined : at.y + 12, bottom: up ? window.innerHeight - at.y + 12 : undefined }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <strong>
        {kind === 'pl'
          ? T(lang, 'No Parallel linked', 'Sin Parallel vinculado', 'Parallel non lié')
          : T(lang, 'No HubSpot link', 'Sin link de HubSpot', 'Aucun lien HubSpot')}
      </strong>
      <p>
        {kind === 'pl'
          ? T(lang,
            'Paste the Parallel link into the ticket’s “Parallel” property in HubSpot — it shows up here on its own.',
            'Pega el link de Parallel en la propiedad “Parallel” del ticket en HubSpot — aquí aparece solo.',
            'Collez le lien Parallel dans la propriété « Parallel » du ticket HubSpot — il apparaît ici tout seul.')
          : T(lang,
            'Search the client in HubSpot: if the ticket exists it arrives on the next sync; if not, create it there.',
            'Busca al cliente en HubSpot: si el ticket existe llega en la próxima sincronización; si no, créalo allí.',
            'Cherchez le client dans HubSpot : s’il existe, il arrive à la prochaine synchro ; sinon, créez-le là-bas.')}
      </p>
      <em>
        {T(lang,
          'Need a hand? Product: Sarah Schnetzer · David Planes · Kevin Florencia.',
          '¿Dudas? Producto: Sarah Schnetzer · David Planes · Kevin Florencia.',
          'Un doute ? Produit : Sarah Schnetzer · David Planes · Kevin Florencia.')}
      </em>
    </div>,
    document.body,
  );
}

/* ---- Brand marks (drawn to match the real logos) + quick-open chips ---- */

/** Parallel — purple three-slice diamond. */
export function ParallelIcon({ size = 14 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <g fill="#6d5df6" stroke="#6d5df6" strokeWidth="1.2" strokeLinejoin="round">
        <path d="M12 2.9c.4 0 .8.15 1.1.44l6.8 6.4c.62.58.2 1.62-.65 1.62H4.75c-.85 0-1.27-1.04-.65-1.62l6.8-6.4c.3-.29.7-.44 1.1-.44z" />
        <path d="M6.3 13.4h11.4l-2.35 3.05H8.65L6.3 13.4z" />
        <path d="M9.9 18.5h4.2l-1.55 2c-.28.36-.82.36-1.1 0l-1.55-2z" />
      </g>
    </svg>
  );
}

/** HubSpot — orange sprocket. */
export function HubSpotIcon({ size = 15 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <g stroke="#ff5c35" strokeWidth="2.5" strokeLinecap="round" fill="none">
        <circle cx="14.6" cy="14.4" r="4.7" />
        <path d="M14.6 9.7V5.6" />
        <path d="M11.2 11.3 4.9 5.4" />
        <path d="M11.6 17.8l-3 3" />
      </g>
      <g fill="#ff5c35">
        <circle cx="14.6" cy="4.1" r="1.9" />
        <circle cx="3.8" cy="4.3" r="2.1" />
        <circle cx="7.6" cy="21.6" r="1.8" />
      </g>
    </svg>
  );
}

/** Circular app shortcut inside a card. A span (not a nested button/anchor —
 *  those are invalid inside the card's <button>) that stops propagation. */
export function QuickApp({ href, title, children, cls }: { href: string; title: string; children: ReactNode; cls?: string }) {
  const open = () => window.open(href, '_blank', 'noopener,noreferrer');
  return (
    <span
      className={`mw-appbtn ${cls || ''}`}
      role="button"
      tabIndex={0}
      title={title}
      onClick={(e) => { e.stopPropagation(); open(); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); open(); }
      }}
    >
      {children}
    </span>
  );
}

/** The case's Drive folder. HubSpot never carried it, so the first person who
 *  has the link pastes it here and the button works for everyone from then on. */
export function DriveButton({ ticketId, url, canEdit, lang }: {
  ticketId: string; url: string; canEdit: boolean; lang: Lang;
}) {
  const m = useCaseDateMutations(ticketId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(url);
  useEffect(() => { setDraft(url); }, [url]);
  const save = () => {
    const v = draft.trim();
    if (v && !/^https?:\/\//i.test(v)) return;
    m.setDrive.mutate(v, { onSuccess: () => setOpen(false) });
  };

  if (url && !open) {
    return (
      <span className="tk-drivewrap">
        <a className="tk-appbtn" href={url} target="_blank" rel="noopener noreferrer"
          title={T(lang, 'Open the case folder in Drive', 'Abrir la carpeta del caso en Drive', 'Ouvrir le dossier dans Drive')}>
          <DriveGlyph />
        </a>
        {canEdit && (
          <button className="tk-drive-edit" onClick={() => setOpen(true)}
            title={T(lang, 'Change the Drive link', 'Cambiar el link de Drive', 'Changer le lien Drive')}>
            <KBIcon name="edit" size={11} />
          </button>
        )}
      </span>
    );
  }

  return (
    <span className="tk-drivewrap">
      <button
        className={`tk-appbtn ${url ? '' : 'empty'}`}
        onClick={() => canEdit && setOpen((v) => !v)}
        disabled={!canEdit}
        title={canEdit
          ? T(lang, 'Add the case folder in Drive', 'Añadir la carpeta del caso en Drive', 'Ajouter le dossier Drive')
          : T(lang, 'No Drive folder on this case', 'Sin carpeta de Drive en este caso', 'Aucun dossier Drive')}
      >
        <DriveGlyph />
      </button>
      {open && canEdit && (
        <AnchoredPop anchor=".tk-drivewrap .tk-appbtn" onClose={() => setOpen(false)} width={300} className="tk-drive-pop">
          <label className="tk-drive-lbl">{T(lang, 'Drive folder for this case', 'Carpeta de Drive de este caso', 'Dossier Drive de ce dossier')}</label>
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
            placeholder="https://drive.google.com/…"
          />
          {m.setDrive.isError && (
            <p className="tk-drive-err">{friendlyError(m.setDrive.error, lang)}</p>
          )}
          <div className="tk-drive-acts">
            {url && (
              <button className="tk-drive-clear" onClick={() => m.setDrive.mutate('', { onSuccess: () => setOpen(false) })}>
                {T(lang, 'Remove', 'Quitar', 'Retirer')}
              </button>
            )}
            <button className="tk-cal-cancel" onClick={() => setOpen(false)}>{T(lang, 'Cancel', 'Cancelar', 'Annuler')}</button>
            <button className="tk-cal-save" onClick={save} disabled={m.setDrive.isPending}>
              {T(lang, 'Save', 'Guardar', 'Enregistrer')}
            </button>
          </div>
        </AnchoredPop>
      )}
    </span>
  );
}

/** Drive's triangular folder, in the brand's four colours. */
export function DriveGlyph() {
  return (
    <svg width="21" height="19" viewBox="0 0 87 76" aria-hidden="true">
      <path fill="#0066da" d="M6.6 66.9 10.4 73.5c.8 1.4 2 2.5 3.4 3.3L27.6 53H0c0 1.6.4 3.2 1.2 4.6z" />
      <path fill="#00ac47" d="M43.5 23 29.7 0c-1.4.8-2.6 1.9-3.4 3.3L1.2 46.4A9.2 9.2 0 0 0 0 51h27.6z" />
      <path fill="#ea4335" d="M73.6 76.8c1.4-.8 2.6-1.9 3.4-3.3l1.6-2.7 7.6-13.2c.8-1.4 1.2-3 1.2-4.6H59.3l5.9 11.5z" />
      <path fill="#00832d" d="M43.5 23 57.3 0H29.7l13.8 23z" />
      <path fill="#2684fc" d="M59.4 51H27.6L13.8 74.8c1.4.8 2.9 1.2 4.5 1.2h50.3c1.6 0 3.2-.4 4.5-1.2z" />
      <path fill="#ffba00" d="M73.4 24.5 61 3.3A9.2 9.2 0 0 0 57.3 0L43.5 23l15.9 28h27.5c0-1.6-.4-3.2-1.2-4.6z" />
    </svg>
  );
}

